<script>
	import NavBarReusable from './NavbarReusable.svelte';
	import { onMount, tick } from 'svelte';

	// Tools (no specific teacher required)
	import TeacherFinderComponent from './TeacherFinderComponent.svelte';
	import AdminCreateTeacherComponent from './AdminCreateTeacherComponent.svelte';
	import TeacherAvailabilityCapacityComponent from './TeacherAvailabilityCapacityComponent.svelte';
	import RefreshDatabaseButtonComponent from './RefreshDatabaseButtonComponent.svelte';
	import ClassDidNotHappenComponent from './ClassDidNotHappenComponent.svelte';
	import TeacherInvoiceComponent from './TeacherInvoiceComponent.svelte';

	// Teacher search + widgets (require selected teacher)
	import TeacherSearchComponent from './TeacherSearchComponent.svelte';
	import TeacherPerformanceRating from './TeacherPerformanceRating.svelte';
	import TeacherInfoComponent from './TeacherInfoComponent.svelte';
	import TeacherPricingModifierComponent from './TeacherPricingModifierComponent.svelte';
	import { normalizeTeacher, mergeTeacher, teacherDisplayName } from './teacherNormalize.js';

	let selectedTeacher = null;
	let teacherSearchRef = null;
	let pendingHashTeacherId = null;
	let lastHydratedHashTeacherId = null;
	let isHydratingFromHash = false;
	let isHydratingSelectedTeacher = false;

	let performanceRefresh = 0;
	let infoRefresh = 0;
	let pricingRefresh = 0;

	const lastTeacherKey = 'adminTeachers:lastTeacher';
	const collapsedKey = 'adminTeachers:collapsed';
	const toolsCollapsedKey = 'adminTeachers:collapsedTools';
	const showToolsKey = 'adminTeachers:showTools';

	let showAdminTools = true;

	let collapsed = {
		performance: false,
		info: true,
		pricing: true,
	};

	const widgetOrder = /** @type {const} */ (['performance', 'info', 'pricing']);

	function widgetTitle(key) {
		switch (key) {
			case 'performance':
				return 'Teacher Performance';
			case 'info':
				return 'Teacher Information';
			case 'pricing':
				return 'Teacher Pricing';
			default:
				return key;
		}
	}

	function normalizeCollapsedState() {
		// Ensure collapsed has all expected keys (no single-open enforcement).
		collapsed = {
			performance: !!collapsed?.performance,
			info: !!collapsed?.info,
			pricing: !!collapsed?.pricing,
		};
		saveCollapsedState();
	}

	$: openWidgetKeys = widgetOrder.filter(k => !collapsed[k]);

	let toolsCollapsed = {
		createTeacher: false,
		findTeachers: false,
		teacherCapacity: false,
		refreshDb: false,
		classIncidents: false,
		teacherInvoices: false,
	};

	let toast = '';
	let toastTimer = null;

	function showToast(message) {
		toast = message;
		if (toastTimer) clearTimeout(toastTimer);
		toastTimer = setTimeout(() => {
			toast = '';
		}, 1200);
	}

	function teacherLabel(teacher) {
		return teacherDisplayName(teacher);
	}

	function teacherHasUsefulMeta(teacher) {
		if (!teacher) return false;
		return !!(teacher.name || teacher.email || teacher.username || teacher.phone_number);
	}

	function getHashParts() {
		const hash = window.location.hash || '#/admin/teachers';
		const qIndex = hash.indexOf('?');
		if (qIndex === -1) return { path: hash, params: new URLSearchParams() };
		return { path: hash.slice(0, qIndex), params: new URLSearchParams(hash.slice(qIndex + 1)) };
	}

	function getHashQueryParam(name) {
		const { params } = getHashParts();
		return params.get(name);
	}

	function getHashTeacherId() {
		return getHashQueryParam('teacherId');
	}

	function setTeachersHashTeacherId(id) {
		const { path, params } = getHashParts();
		// Only mutate when we're on Teachers page route
		if (!path.startsWith('#/admin/teachers')) return;
		if (id) params.set('teacherId', String(id));
		else params.delete('teacherId');
		const nextHash = params.toString() ? `${path}?${params.toString()}` : path;
		if (nextHash !== window.location.hash) window.location.hash = nextHash;
	}

	function persistSelectedTeacher(teacher) {
		try {
			if (!teacher) {
				localStorage.removeItem(lastTeacherKey);
				return;
			}
			localStorage.setItem(lastTeacherKey, JSON.stringify(teacher));
		} catch {
			// ignore
		}
	}

	function loadCollapsedState() {
		try {
			const raw = localStorage.getItem(collapsedKey);
			if (!raw) return;
			const parsed = JSON.parse(raw);
			if (!parsed || typeof parsed !== 'object') return;
			collapsed = {
				performance: !!parsed.performance,
				info: !!parsed.info,
				pricing: !!parsed.pricing,
			};
		} catch {
			// ignore
		}
	}

	function saveCollapsedState() {
		try {
			localStorage.setItem(collapsedKey, JSON.stringify(collapsed));
		} catch {
			// ignore
		}
	}

	function openWidget(key) {
		// Multi-open behavior: opening a widget does not close others.
		collapsed = { ...collapsed, [key]: false };
		saveCollapsedState();
	}

	function closeWidget(key) {
		if (!key) return;
		collapsed = { ...collapsed, [key]: true };
		saveCollapsedState();
	}

	function toggleCollapsed(key) {
		// Multi-open behavior: toggle only this widget.
		collapsed = { ...collapsed, [key]: !collapsed[key] };
		saveCollapsedState();
	}

	function loadToolsCollapsedState() {
		try {
			const raw = localStorage.getItem(toolsCollapsedKey);
			if (!raw) return;
			const parsed = JSON.parse(raw);
			if (!parsed || typeof parsed !== 'object') return;
			toolsCollapsed = {
				createTeacher: !!parsed.createTeacher,
				findTeachers: !!parsed.findTeachers,
				teacherCapacity: !!parsed.teacherCapacity,
				refreshDb: !!parsed.refreshDb,
				classIncidents: !!parsed.classIncidents,
				teacherInvoices: !!parsed.teacherInvoices,
			};
		} catch {
			// ignore
		}
	}

	function saveToolsCollapsedState() {
		try {
			localStorage.setItem(toolsCollapsedKey, JSON.stringify(toolsCollapsed));
		} catch {
			// ignore
		}
	}

	function toggleToolsCollapsed(key) {
		toolsCollapsed = { ...toolsCollapsed, [key]: !toolsCollapsed[key] };
		saveToolsCollapsedState();
	}

	function closeAllWidgets() {
		collapsed = {
			performance: true,
			info: true,
			pricing: true,
		};
		saveCollapsedState();
	}

	function loadShowToolsState() {
		try {
			const raw = localStorage.getItem(showToolsKey);
			if (raw === null) return;
			showAdminTools = raw === 'true';
		} catch {
			// ignore
		}
	}

	function toggleTools() {
		showAdminTools = !showAdminTools;
		try {
			localStorage.setItem(showToolsKey, String(showAdminTools));
		} catch {
			// ignore
		}
	}

	async function copyToClipboard(text, label) {
		if (!text) return;
		try {
			if (navigator?.clipboard?.writeText) {
				await navigator.clipboard.writeText(text);
			} else {
				const textarea = document.createElement('textarea');
				textarea.value = text;
				textarea.style.position = 'fixed';
				textarea.style.left = '-9999px';
				document.body.appendChild(textarea);
				textarea.focus();
				textarea.select();
				document.execCommand('copy');
				document.body.removeChild(textarea);
			}
			showToast(`Copied ${label}`);
		} catch {
			showToast('Copy failed');
		}
	}

	function jumpTo(key) {
		// Previous behavior was scrolling to a widget section.
		// With the dock model, we open the widget instead.
		openWidget(key);
	}

	function handleTeacherSelected(e) {
		applySelectedTeacher(e.detail);
	}

	async function handleTeacherCreated(e) {
		await teacherSearchRef?.refreshTeachers?.();
		const teacher = await teacherSearchRef?.resolveTeacherById?.(e.detail.teacher_id);
		if (teacher) applySelectedTeacher(teacher);
	}

	async function handleOpenTeacher(e) {
		await handleTeacherCreated(e);
		if (selectedTeacher?.id !== e.detail.teacher_id) {
			applySelectedTeacher({ ...e.detail, id: e.detail.teacher_id,
				name: `${e.detail.first_name} ${e.detail.last_name}`.trim() });
		}
		openWidget('info');
		await tick();
		const panel = document.getElementById('teachers-widget-info');
		panel?.focus({ preventScroll: true });
		panel?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
	}

	function handleTeacherCleared() {
		selectedTeacher = null;
		persistSelectedTeacher(null);
		setTeachersHashTeacherId(null);
	}

	function applySelectedTeacher(teacher) {
		selectedTeacher = normalizeTeacher(teacher);
		performanceRefresh += 1;
		infoRefresh += 1;
		pricingRefresh += 1;
		persistSelectedTeacher(selectedTeacher);
		setTeachersHashTeacherId(selectedTeacher?.id || null);
	}

	async function hydrateFromHash() {
		if (!teacherSearchRef?.resolveTeacherById) return;
		const fromHash = getHashTeacherId();
		if (!fromHash) return;

		const idNum = parseInt(fromHash, 10);
		if (Number.isNaN(idNum)) return;

		// Avoid duplicate hydrations for same hash value when nothing changed.
		if (lastHydratedHashTeacherId === fromHash && selectedTeacher?.id === idNum) return;

		isHydratingFromHash = true;
		try {
			const resolved = await teacherSearchRef.resolveTeacherById(idNum);
			if (resolved) {
				lastHydratedHashTeacherId = fromHash;
				applySelectedTeacher(resolved);
			}
		} finally {
			isHydratingFromHash = false;
		}
	}

	function restoreSelectedTeacherFromStorage() {
		try {
			const raw = localStorage.getItem(lastTeacherKey);
			if (!raw) return;
			const parsed = JSON.parse(raw);
			if (parsed && parsed.id) {
				selectedTeacher = normalizeTeacher(parsed);
				performanceRefresh += 1;
				infoRefresh += 1;
				pricingRefresh += 1;
				// Do not write hash here; hash should be controlled by user selection or deep-link hydration.
			}
		} catch {
			// ignore
		}
	}

	onMount(() => {
		loadShowToolsState();
		loadCollapsedState();
		normalizeCollapsedState();
		loadToolsCollapsedState();
		// 1) Restore last selection immediately (fast UI feedback)
		restoreSelectedTeacherFromStorage();

		// 2) If URL has teacherId, prefer it (may need to wait until TeacherSearchComponent loads)
		const fromHash = getHashTeacherId();
		if (fromHash) pendingHashTeacherId = fromHash;

		// 3) React to back/forward changes
		const onHashChange = () => {
			const next = getHashTeacherId();
			pendingHashTeacherId = next;
			// If cleared, clear selection to match URL.
			if (!next) handleTeacherCleared();
		};
		window.addEventListener('hashchange', onHashChange);

		return () => {
			window.removeEventListener('hashchange', onHashChange);
		};
	});

	// When TeacherSearchComponent is ready, hydrate from hash if needed.
	$: if (teacherSearchRef && pendingHashTeacherId && !isHydratingFromHash) {
		// Only hydrate if the pending value differs from current selection
		const idNum = parseInt(pendingHashTeacherId, 10);
		if (!Number.isNaN(idNum) && selectedTeacher?.id !== idNum) {
			hydrateFromHash();
		}
	}

	// When selection came from storage (or older cached object), hydrate missing meta fields.
	$: if (teacherSearchRef?.resolveTeacherById && selectedTeacher?.id && !teacherHasUsefulMeta(selectedTeacher) && !isHydratingSelectedTeacher) {
		isHydratingSelectedTeacher = true;
		Promise.resolve(teacherSearchRef.resolveTeacherById(selectedTeacher.id))
			.then((resolved) => {
				if (resolved) {
					// Merge so we keep any extra fields from storage, but prefer fresh meta.
					selectedTeacher = mergeTeacher(selectedTeacher, resolved);
					persistSelectedTeacher(selectedTeacher);
				}
			})
			.finally(() => {
				isHydratingSelectedTeacher = false;
			});
	}
</script>

<div class="app-container">
	<NavBarReusable label="Admin - Teachers" />
	<div class="calendar-launch"><a class="btn" href="#/admin/calendar" target="_blank" rel="noopener noreferrer">Open Tutory Calendar</a></div>

	<div class="page">
		<section class="section">
			<div class="section-title-row">
				<h2 class="section-title">Admin tools</h2>
				<button type="button" class="btn small" on:click={toggleTools}>
					{showAdminTools ? 'Hide tools' : 'Show tools'}
				</button>
			</div>

			{#if showAdminTools}
				<div class="tools-grid">
					<div class="card tool tool-create-teacher">
						<div class="widget-header">
							<button type="button" class="widget-title" aria-controls="teachers-tool-create-body"
								aria-expanded={!toolsCollapsed.createTeacher} on:click={() => toggleToolsCollapsed('createTeacher')}>
								<span class="chevron" aria-hidden="true">{toolsCollapsed.createTeacher ? '▸' : '▾'}</span>
								Teacher account creation
							</button>
						</div>
						{#if !toolsCollapsed.createTeacher}<div class="widget-body" id="teachers-tool-create-body">
							<AdminCreateTeacherComponent on:created={handleTeacherCreated} on:openTeacher={handleOpenTeacher} />
						</div>{/if}
					</div>
					<div class="card tool tool-find-teachers">
						<div class="widget-header">
							<button
								type="button"
								class="widget-title"
								aria-controls="teachers-tool-findTeachers-body"
								aria-expanded={!toolsCollapsed.findTeachers}
								on:click={() => toggleToolsCollapsed('findTeachers')}
							>
								<span class="chevron" aria-hidden="true">{toolsCollapsed.findTeachers ? '▸' : '▾'}</span>
								Find Teachers
							</button>
						</div>
						{#if !toolsCollapsed.findTeachers}
							<div class="widget-body" id="teachers-tool-findTeachers-body">
								<TeacherFinderComponent />
							</div>
						{/if}
					</div>

					<div class="card tool tool-teacher-capacity">
						<div class="widget-header">
							<button
								type="button"
								class="widget-title"
								aria-controls="teachers-tool-teacherCapacity-body"
								aria-expanded={!toolsCollapsed.teacherCapacity}
								on:click={() => toggleToolsCollapsed('teacherCapacity')}
							>
								<span class="chevron" aria-hidden="true">{toolsCollapsed.teacherCapacity ? '▸' : '▾'}</span>
								Availability Capacity
							</button>
						</div>
						{#if !toolsCollapsed.teacherCapacity}
							<div class="widget-body" id="teachers-tool-teacherCapacity-body">
								<TeacherAvailabilityCapacityComponent />
							</div>
						{/if}
					</div>

					<div class="card tool tool-db-refresh">
						<div class="widget-header">
							<button
								type="button"
								class="widget-title"
								aria-controls="teachers-tool-refreshDb-body"
								aria-expanded={!toolsCollapsed.refreshDb}
								on:click={() => toggleToolsCollapsed('refreshDb')}
							>
								<span class="chevron" aria-hidden="true">{toolsCollapsed.refreshDb ? '▸' : '▾'}</span>
								Database refresh
							</button>
						</div>
						{#if !toolsCollapsed.refreshDb}
							<div class="widget-body" id="teachers-tool-refreshDb-body">
								<RefreshDatabaseButtonComponent />
							</div>
						{/if}
					</div>

					<div class="card tool tool-incidents">
						<div class="widget-header">
							<button
								type="button"
								class="widget-title"
								aria-controls="teachers-tool-incidents-body"
								aria-expanded={!toolsCollapsed.classIncidents}
								on:click={() => toggleToolsCollapsed('classIncidents')}
							>
								<span class="chevron" aria-hidden="true">{toolsCollapsed.classIncidents ? '▸' : '▾'}</span>
								Class Incidents
							</button>
						</div>
						{#if !toolsCollapsed.classIncidents}
							<div class="widget-body" id="teachers-tool-incidents-body">
								<ClassDidNotHappenComponent />
							</div>
						{/if}
					</div>

					<div class="card tool tool-invoices">
						<div class="widget-header">
							<button
								type="button"
								class="widget-title"
								aria-controls="teachers-tool-invoices-body"
								aria-expanded={!toolsCollapsed.teacherInvoices}
								on:click={() => toggleToolsCollapsed('teacherInvoices')}
							>
								<span class="chevron" aria-hidden="true">{toolsCollapsed.teacherInvoices ? '▸' : '▾'}</span>
								Teacher Invoices
							</button>
						</div>
						{#if !toolsCollapsed.teacherInvoices}
							<div class="widget-body" id="teachers-tool-invoices-body">
								<TeacherInvoiceComponent />
							</div>
						{/if}
					</div>
				</div>
			{/if}
		</section>

		<section class="section">
			<h2 class="section-title">Find teacher</h2>
			<TeacherSearchComponent
				bind:this={teacherSearchRef}
				{selectedTeacher}
				on:teacherSelected={handleTeacherSelected}
				on:teacherCleared={handleTeacherCleared}
			/>
		</section>

		<section class="section">
			<h2 class="section-title">Teacher dashboard</h2>

			{#if !selectedTeacher}
				<div class="empty">
					Select a teacher to load performance, information, and pricing.
				</div>
			{:else}
				<div class="selected-row">
					<div class="selected-meta">
						<strong>{teacherLabel(selectedTeacher)}</strong>
						<span class="muted">
							• ID: {selectedTeacher.id}
							{#if selectedTeacher.username}
								• @{selectedTeacher.username}
							{/if}
							{#if selectedTeacher.email}
								• {selectedTeacher.email}
							{/if}
							{#if selectedTeacher.phone_number}
								• {selectedTeacher.phone_number}
							{/if}
						</span>

						<div class="quick-actions">
							<button type="button" class="btn small" on:click={() => copyToClipboard(String(selectedTeacher.id), 'ID')}>Copy ID</button>
							<button type="button" class="btn small" disabled={!selectedTeacher.email} on:click={() => copyToClipboard(selectedTeacher.email, 'email')}>Copy email</button>
							<button type="button" class="btn small" disabled={!selectedTeacher.username} on:click={() => copyToClipboard(selectedTeacher.username, 'username')}>Copy username</button>
							<button type="button" class="btn small" disabled={!selectedTeacher.phone_number} on:click={() => copyToClipboard(selectedTeacher.phone_number, 'phone')}>Copy phone</button>
							<button type="button" class="btn small" on:click={() => openWidget('performance')}>Performance</button>
							<button type="button" class="btn small" on:click={() => openWidget('info')}>Info</button>
							<button type="button" class="btn small" on:click={() => openWidget('pricing')}>Pricing</button>
						</div>
					</div>
					<div class="selected-actions">
					<a class="btn" href="#/admin/calendar" target="_blank" rel="noopener noreferrer">Open Tutory Calendar</a>
						<button type="button" class="btn small" disabled={openWidgetKeys.length === 0} on:click={closeAllWidgets}>
							Close all widgets
						</button>
						<button type="button" class="btn" on:click={handleTeacherCleared}>Clear</button>
					</div>
				</div>

				{#if toast}
					<div class="toast" role="status">{toast}</div>
				{/if}

				{#if widgetOrder.some(k => collapsed[k])}
					<div class="widget-dock" aria-label="Closed widgets">
						<div class="widget-dock-label">Closed widgets</div>
						<div class="widget-dock-buttons">
							{#each widgetOrder as key (key)}
								{#if collapsed[key]}
									<button type="button" class="dock-btn" on:click={() => openWidget(key)}>
										{widgetTitle(key)}
									</button>
								{/if}
							{/each}
						</div>
					</div>
				{/if}

				<div class="widgets-grid">
					{#if openWidgetKeys.length === 0}
						<div class="empty widgets-empty">Select a widget above to open.</div>
					{:else}
						{#each openWidgetKeys as key (key)}
							{#if key === 'performance'}
								<div class="card widget performance-card" id="teachers-widget-performance">
									<div class="widget-header">
										<button
											type="button"
											class="widget-title"
											aria-controls="teachers-widget-performance-body"
											aria-expanded={!collapsed.performance}
											on:click={() => toggleCollapsed('performance')}
										>
											<span class="chevron" aria-hidden="true">{collapsed.performance ? '▸' : '▾'}</span>
											Teacher Performance
										</button>
										<div class="widget-actions">
											<a class="link" href={`#/teacherSurveilance?tab=performance&teacherId=${selectedTeacher.id}`} target="_blank" rel="noreferrer">Open full</a>
											<button type="button" class="btn small" on:click={() => (performanceRefresh += 1)}>Reload</button>
											<button type="button" class="btn small" on:click={() => closeWidget('performance')}>Close</button>
										</div>
									</div>
									<div class="widget-body scroll" id="teachers-widget-performance-body">
										{#key performanceRefresh}
											<TeacherPerformanceRating teacherId={selectedTeacher.id} embedded={true} />
										{/key}
									</div>
								</div>
							{/if}

							{#if key === 'info'}
								<div class="card widget info-card" id="teachers-widget-info" role="region" aria-label="Teacher information panel" tabindex="-1">
									<div class="widget-header">
										<button
											type="button"
											class="widget-title"
											aria-controls="teachers-widget-info-body"
											aria-expanded={!collapsed.info}
											on:click={() => toggleCollapsed('info')}
										>
											<span class="chevron" aria-hidden="true">{collapsed.info ? '▸' : '▾'}</span>
											Teacher Information
										</button>
										<div class="widget-actions">
											<a class="link" href={`#/teacherSurveilance?tab=teacher-info&teacherId=${selectedTeacher.id}`} target="_blank" rel="noreferrer">Open full</a>
											<button type="button" class="btn small" on:click={() => (infoRefresh += 1)}>Reload</button>
											<button type="button" class="btn small" on:click={() => closeWidget('info')}>Close</button>
										</div>
									</div>
									<div class="widget-body scroll" id="teachers-widget-info-body">
										{#key infoRefresh}
											<TeacherInfoComponent teacherId={selectedTeacher.id} teacher={selectedTeacher} embedded={true} />
										{/key}
									</div>
								</div>
							{/if}

							{#if key === 'pricing'}
								<div class="card widget pricing-card" id="teachers-widget-pricing">
									<div class="widget-header">
										<button
											type="button"
											class="widget-title"
											aria-controls="teachers-widget-pricing-body"
											aria-expanded={!collapsed.pricing}
											on:click={() => toggleCollapsed('pricing')}
										>
											<span class="chevron" aria-hidden="true">{collapsed.pricing ? '▸' : '▾'}</span>
											Teacher Pricing
										</button>
										<div class="widget-actions">
											<a class="link" href={`#/admin?tab=teacher-pricing&teacherId=${selectedTeacher.id}`} target="_blank" rel="noreferrer">Open full</a>
											<button type="button" class="btn small" on:click={() => (pricingRefresh += 1)}>Reload</button>
											<button type="button" class="btn small" on:click={() => closeWidget('pricing')}>Close</button>
										</div>
									</div>
									<div class="widget-body" id="teachers-widget-pricing-body">
										{#key pricingRefresh}
											<TeacherPricingModifierComponent teacherId={selectedTeacher.id} teacher={selectedTeacher} embedded={true} />
										{/key}
									</div>
								</div>
							{/if}
						{/each}
					{/if}
				</div>
			{/if}
		</section>
	</div>
</div>

<style>
	.calendar-launch { padding: 18px 24px 0; }
	.calendar-launch .btn { display: inline-block; padding: 14px 24px; background: #273c75; color: white; text-decoration: none; border-radius: 12px; font-weight: 700; }
	.page {
		padding: 18px 22px 30px;
		background: #f6f7fb;
	}

	.section {
		margin-bottom: 22px;
	}

	.section-title {
		margin: 0 0 10px 0;
		font-size: 18px;
	}

	.section-title-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		margin: 0 0 10px 0;
	}

	.tools-grid {
		column-count: 2;
		column-gap: 24px;
	}

	.tools-grid > .card {
		break-inside: avoid;
		display: flex;
		width: 100%;
		box-sizing: border-box;
		margin: 0 0 16px;
	}

	.widgets-grid {
		/* Match tools-grid stacking behavior (masonry-style columns). */
		column-count: 2;
		column-gap: 16px;
	}

	.widgets-grid > .card {
		break-inside: avoid;
		display: flex;
		width: 100%;
		box-sizing: border-box;
		margin: 0 0 16px;
	}

	.widget-dock {
		margin: 10px 0 14px;
		padding: 12px;
		border-radius: 10px;
		background: rgba(255, 255, 255, 0.7);
		border: 1px solid rgba(0, 0, 0, 0.08);
	}

	.widget-dock-label {
		font-weight: 800;
		color: #1f2937;
		margin-bottom: 8px;
	}

	.widget-dock-buttons {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}

	.dock-btn {
		padding: 6px 10px;
		border: 1px solid rgba(0, 0, 0, 0.14);
		background: rgba(255, 255, 255, 0.95);
		border-radius: 999px;
		cursor: pointer;
		font-size: 12px;
		font-weight: 700;
		color: #111827;
	}

	.dock-btn:hover {
		background: white;
	}

	.widgets-empty {
		column-span: all;
	}

	.card {
		padding: 15px;
		background-color: white;
		display: flex;
		flex-direction: column;
		gap: 10px;
		box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
		border-radius: 8px;
		color: black;
		align-items: stretch;
	}

	.card.widget,
	.card.tool {
		background: var(--tint, #fff);
		border-left: 6px solid var(--accent, transparent);
	}

	.tool-find-teachers {
		--accent: #0ea5e9;
		--tint: #e0f2fe;
	}
	.tool-db-refresh {
		--accent: #6b7280;
		--tint: #f3f4f6;
	}
	.tool-incidents {
		--accent: #dc2626;
		--tint: #fef2f2;
	}
	.tool-invoices {
		--accent: #059669;
		--tint: #ecfdf5;
	}

	.performance-card {
		--accent: #f59e0b;
		--tint: #fffbeb;
	}
	.info-card {
		--accent: #2563eb;
		--tint: #eff6ff;
	}
	.pricing-card {
		--accent: #7c3aed;
		--tint: #f5f3ff;
	}

	.widget-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
	}

	.widget-actions {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.widget-title {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 6px 8px;
		border: 0;
		background: transparent;
		font-weight: 800;
		color: #1f2937;
		cursor: pointer;
		border-radius: 8px;
		text-align: left;
	}

	.widget-title:hover {
		background: rgba(255, 255, 255, 0.7);
	}

	.widget-title:focus {
		outline: 2px solid rgba(20, 105, 91, 0.4);
		outline-offset: 2px;
	}

	.chevron {
		font-size: 14px;
		opacity: 0.9;
	}

	.widget-body {
		width: 100%;
	}

	.widget-body.scroll {
		max-height: 650px;
		overflow: auto;
		padding: 10px;
		border-radius: 8px;
		border: 1px solid rgba(0, 0, 0, 0.08);
		background: rgba(255, 255, 255, 0.75);
	}

	.selected-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
		margin: 10px 0 14px;
		padding: 10px 12px;
		border-radius: 8px;
		background: rgba(248, 249, 250, 0.92);
		backdrop-filter: blur(8px);
		border: 1px solid rgba(0, 0, 0, 0.08);
		position: sticky;
		top: 10px;
		z-index: 20;
	}

	.quick-actions {
		margin-top: 8px;
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}

	.toast {
		margin: 10px 0 14px;
		padding: 10px 12px;
		border-radius: 8px;
		background: #e7f3ff;
		border: 1px solid #bcdcff;
		color: #0b3d69;
	}

	.muted {
		color: #666;
		font-weight: 400;
	}

	.btn {
		padding: 8px 12px;
		border: 1px solid #ccc;
		background: white;
		border-radius: 8px;
		cursor: pointer;
		white-space: nowrap;
	}

	.btn.small {
		padding: 6px 10px;
		border-radius: 6px;
		font-size: 12px;
	}

	.empty {
		padding: 18px 16px;
		border-radius: 8px;
		background: #f8f9fa;
		border: 1px solid #e0e0e0;
		color: #666;
	}

	@media (max-width: 1400px) {
		.tools-grid {
			column-count: 1;
		}
	}

	@media (max-width: 1100px) {
		.widgets-grid {
			column-count: 1;
		}
	}
</style>

